import { el } from './dom';
import { ageBandFor, type AgeBand } from '../config/ages';
import type { StorageLike } from './saveSync';

/**
 * The neutral age screen that precedes signup.
 *
 * "When were you born?" with two blank selects — deliberately neutral. "Are you
 * 14 or older?" telegraphs which answer avoids the longer path, and a child who
 * wants a cloud save will take the hint.
 *
 * Only the derived band is persisted. The month and year are read, used once,
 * and discarded: storing a child's date of birth in order to decide that we
 * should store nothing else would defeat the whole design.
 *
 * Spec: docs/superpowers/specs/2026-09-12-privacy-compliance-design.md §4.
 */

export const AGE_KEY = 'hambi.ageBand';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Storage is injected so this stays testable in the node environment, matching
 * the pattern applyGive and saveSync already use. Defaults to real storage.
 */
function defaultStorage(): StorageLike | null {
  try {
    return window.localStorage;
  } catch {
    // Private windows throw on mere access.
    return null;
  }
}

/** The answer is remembered so it cannot be trivially re-rolled on a retry. */
export function rememberedBand(storage: StorageLike | null = defaultStorage()): AgeBand | null {
  try {
    const raw = storage?.getItem(AGE_KEY) ?? null;
    return raw === 'child' || raw === 'general' ? raw : null;
  } catch {
    return null;
  }
}

export function rememberBand(band: AgeBand, storage: StorageLike | null = defaultStorage()): void {
  try {
    storage?.setItem(AGE_KEY, band);
  } catch {
    // Non-fatal: the gate simply asks again next time.
  }
}

export function forgetBand(storage: StorageLike | null = defaultStorage()): void {
  try {
    storage?.removeItem(AGE_KEY);
  } catch {
    // Non-fatal.
  }
}

/**
 * Builds the screen. `onChosen` receives the band; the caller decides which
 * signup form to show next.
 */
export function buildAgeGate(onChosen: (band: AgeBand) => void): HTMLDivElement {
  const month = el('select', { testid: 'age-month' });
  month.append(new Option('Month', ''));
  MONTHS.forEach((name, i) => month.append(new Option(name, String(i + 1))));

  const year = el('select', { testid: 'age-year' });
  year.append(new Option('Year', ''));
  const thisYear = new Date().getUTCFullYear();
  for (let y = thisYear; y >= thisYear - 100; y--) year.append(new Option(String(y), String(y)));

  const submit = el('button', { testid: 'age-continue' }, 'Continue');
  submit.disabled = true;

  const check = (): void => {
    submit.disabled = !month.value || !year.value;
  };
  month.addEventListener('change', check);
  year.addEventListener('change', check);

  submit.addEventListener('click', () => {
    if (!month.value || !year.value) return;
    const band = ageBandFor(Number(year.value), Number(month.value), new Date());
    // The date itself goes no further than this line.
    rememberBand(band);
    onChosen(band);
  });

  return el(
    'div',
    { className: 'account-panel', testid: 'account-panel-age' },
    el('h2', {}, 'When were you born?'),
    el(
      'div',
      { className: 'account-hint' },
      'We only keep whether you are under 14 — never your birthday.',
    ),
    el('div', { className: 'age-row' }, month, year),
    submit,
  );
}
